// @ts-check
import { route } from './author.js';
/** @param {import('../../game/types.js').LevelDefinition} source @param {string} id @param {boolean} [finale] @param {string} [next] */
const quantum = (source, id, finale = false, next) => {
  // Long fractures require intermediate landings; foothold widths stay precise.
  const d = finale ? { ...source, width: source.width * 1.65, finish: source.finish * 1.65,
    solids: source.solids.map(s => ({ ...s, x: s.x * 1.65 })),
    gaps: source.gaps.map(g => /** @type {[number,number]} */ ([g[0] * 1.65, g[1] * 1.65])),
    mechanisms: source.mechanisms?.map(m => ({ ...m, x: m.x * 1.65 })),
    checkpoints: source.checkpoints?.map(c => ({ ...c, x: c.x * 1.65 })),
  } : source;
  return Object.freeze({ ...d, id, quantum: true, quantumFinale: finale, secretNext: next,
  width: Math.max(d.width, d.finish + (finale ? 420 : 180)), bandwidthGoal: finale,
  ...(finale ? {} : { transistors: Object.freeze([Object.freeze({ x: 15 * 32, y: 360, slot: 1 })]) }),
  solids: Object.freeze([...d.solids.map(s => ({ ...s, oneWay: s.kind !== 'node' })),
    { id: 'ceiling', x: 0, y: finale ? 608 : 448, width: Math.max(d.width, d.finish + (finale ? 420 : 180)), height: d.height - (finale ? 608 : 448), kind: /** @type {const} */ ('compute'), ceiling: true }]),
});
};
// One small, distinct pocket for each world. Their middle transistor belongs to the parent.
export const QUANTUM_POCKETS = Object.freeze([
  quantum(route(9,0,'Between the Bits','2 nm · Keep an air jump for the high transistor.',24)
    .device('spring',5,0,2,.5).gap(9,6).platform(10,3,2,.5).platform(14,5,2,.5)
    .node(18,4,'bit').build(),'quantum-pocket'),
  quantum(route(9,0,'Packet Slipstream','2 nm · Ride the moving packet, then jump for the middle slot.',24)
    .platform(6,2,2,.5).gap(9,8).device('carrier',11,3,3,.5,4,{travelX:3})
    .platform(15,5,2,.5).node(19,4,'bit').build(),'quantum-pocket-2'),
  quantum(route(9,0,'The Impossible Cache','2 nm · The memory ledge remembers your last landing.',24)
    .platform(6,2,2,.5).gap(9,8).device('memory',11,4,3,.5,3)
    .platform(15,6,2,.5).node(19,4,'bit').build(),'quantum-bonus'),
  quantum(route(9,0,'Between Ticks','2 nm · Wait for the membrane, then commit to the jump.',24)
    .platform(6,2,2,.5).gap(9,8).device('phase',11,4,3,.5,3)
    .platform(15,6,2,.5).node(19,4,'bit').build(),'quantum-pocket-4'),
  quantum(route(9,0,'Hidden Frame','2 nm · Small islands. Long arcs. One careful landing.',24)
    .platform(6,2,2,.5).gap(9,8).platform(10,4,1.5,.5)
    .platform(15,6,1.5,.5).node(19,4,'bit').build(),'quantum-pocket-5'),
  quantum(route(9,0,'Cold Spark','2 nm · Catch the rising membrane and steer through the foam.',24)
    .platform(6,2,2,.5).gap(9,8).device('carrier',10,3,3,.5,3,{travelY:2})
    .platform(15,6,2,.5).node(19,4,'bit').build(),'quantum-pocket-6'),
  quantum(route(9,0,'Blind Spot','2 nm · Bounce into the quiet space above the scanner.',24)
    .device('spring',5,0,2,.5).gap(9,8).device('memory',10,4,2,.5,3)
    .platform(15,6,2,.5).node(19,4,'bit').build(),'quantum-pocket-7'),
  quantum(route(9,0,'Kernel Whisper','2 nm · Use the last air jump to catch the tiny upper ledge.',24)
    .platform(6,2,2,.5).gap(9,8).device('carrier',10,3,2,.5,3,{travelX:3})
    .platform(15,6,1.5,.5).node(19,4,'bit').build(),'quantum-pocket-8'),
]);
// The post-campaign world extends the pocket mechanics into sustained precision routes.
export const QUANTUM_WORLD = Object.freeze([
  quantum(route(9,1,'Quantum Foam','96 transistors · The tiny universe opens. Keep one jump in reserve.',88)
    .device('spring',7,0,2,.5).gap(12,15).platform(17,5,2,.5).platform(24,8,2,.5)
    .gap(31,15).device('carrier',34,6,2,.5,4,{travelY:3}).platform(43,9,2,.5)
    .checkpoint(49,'Foam anchor').gap(54,17).device('memory',57,6,2,.5,3)
    .platform(65,9,1.5,.5).platform(75,5,2,.5).node(79,5,'upgrade').build(),'quantum-1',true,'quantum-2'),
  quantum(route(9,2,'Entangled Paths','Pair the moving membranes. A late second jump saves the crossing.',96)
    .platform(7,3,2,.5).gap(11,20).device('carrier',14,5,2,.5,3,{travelX:5})
    .device('carrier',24,8,2,.5,4,{travelY:-3}).platform(34,5,2,.5)
    .checkpoint(42,'Pair anchor').gap(47,22).device('carrier',50,6,2,.5,3,{travelY:3})
    .device('carrier',60,9,2,.5,4,{travelX:5}).platform(73,6,2,.5)
    .gap(79,9).platform(82,6,1.5,.5).build(),'quantum-2',true,'quantum-3'),
  quantum(route(9,3,'Vacuum Garden','Brief footholds bloom and fade. Land, breathe, leap.',96)
    .device('spring',7,0,2,.5).gap(12,22).device('memory',16,5,2,.5,3)
    .device('phase',25,8,2,.5,3).platform(37,5,2,.5)
    .checkpoint(43,'Garden anchor').gap(48,23).device('memory',51,6,2,.5,3)
    .device('phase',62,9,2,.5,3).platform(75,6,2,.5)
    .gap(81,8).platform(84,7,1.5,.5).build(),'quantum-3',true,'quantum-4'),
  quantum(route(9,4,'The 2 nm Horizon','The final circuit combines every quantum move. Make each landing count.',104)
    .device('spring',7,0,2,.5).gap(12,22).device('carrier',16,6,2,.5,3,{travelX:4})
    .device('memory',27,9,1.5,.5,3).platform(37,6,1.5,.5)
    .checkpoint(44,'Horizon anchor').gap(49,26).device('phase',52,6,2,.5,3)
    .device('carrier',62,9,1.5,.5,3,{travelY:-3}).device('memory',71,8,1.5,.5,3)
    .platform(79,5,2,.5).gap(85,11).platform(89,8,1.5,.5).build(),'quantum-4',true),
]);
export const QUANTUM_LEVELS = Object.freeze([...QUANTUM_POCKETS, ...QUANTUM_WORLD]);
